/**
 * Pure helpers for cart page display and calculations.
 * No API calls; same inputs/outputs as previous inline logic.
 */
import { CustomerGroup } from '../../home/types/home.types';
import { CartView } from '../types/cartTypes';

interface MoneyLike {
  centAmount?: number;
  currencyCode?: string;
  value?: { centAmount?: number; currencyCode?: string };
}

/**
 * Sum of discount amounts across line items (from discountedPricePerQuantity).
 * @param {Object} cart - Cart with lineItems
 * @param {string} [currency] - Currency code (defaults to cart.totalPrice.currencyCode or 'USD')
 * @returns {{ centAmount: number, currencyCode: string }}
 */
export function getTotalDiscountAmount(cart: CartView | null, currency?: string) {
  const currencyCode = currency || cart?.currency || 'USD';
  const total = { centAmount: 0, currencyCode };
  if (!cart?.lineItems) return total;

  cart.lineItems.forEach((item) => {
    item.discountedPricePerQuantity?.forEach((dppq: { quantity?: number; discountedPrice?: { includedDiscounts?: Array<{ discountedAmount?: { centAmount?: number } }> } }) => {
      dppq.discountedPrice?.includedDiscounts?.forEach((included) => {
        total.centAmount += (included.discountedAmount?.centAmount || 0) * (dppq.quantity || 1);
      });
    });
  });
  return total;
}

/**
 * Multiply a money object by quantity (for synthetic line items).
 * @param {Object} money - { value?: { centAmount, currencyCode }, centAmount?, currencyCode? }
 * @param {number} qty - Quantity
 * @param {string} [fallbackCurrency='USD']
 * @returns {{ centAmount: number, currencyCode: string }}
 */
export function moneyTimesQty(money: MoneyLike | null | undefined, qty: number, fallbackCurrency = 'USD') {
  if (!money) return { centAmount: 0, currencyCode: fallbackCurrency };
  const base = money.value || money;
  return {
    centAmount: (base.centAmount || 0) * qty,
    currencyCode: base.currencyCode || fallbackCurrency,
  };
}

/**
 * Get showAddOns / showOptions toggles for a delegate rate from customer groups.
 * @param {string} delegateRateName - Name of the delegate rate (e.g. from line item custom field)
 * @param {Array} customerGroups - Event customer groups with custom.fields
 * @param {string} [locale] - Locale for group name lookup (e.g. config.locale)
 * @returns {{ showAddOns: boolean, showOptions: boolean }}
 */
export function getDelegateRateToggles(delegateRateName: string, customerGroups: CustomerGroup[], locale = 'en') {
  if (!delegateRateName || !customerGroups?.length) {
    return { showAddOns: true, showOptions: true };
  }

  const matchedGroup = customerGroups.find((g) => {
    const name = g.name;
    const groupName = name ? (typeof name === 'object' ? name[locale] : name) : undefined;
    return groupName === delegateRateName;
  });

  if (!matchedGroup) {
    return { showAddOns: true, showOptions: true };
  }

  const fields = matchedGroup.custom?.fields || {};
  return {
    showAddOns: fields['addonsToggle'] === 'enable',
    showOptions: fields['optionsToggle'] === 'enable',
  };
}
/**
 * Identifies if a line item (raw or display-mapped) is a "Pass" product
 * based on the presence of typical pass-specific attributes or custom fields.
 * Used for grouping and distinguishing main items from add-ons.
 */
export function isPassProduct(item: { 
  variant?: { attributes?: Array<{ name: string; value: unknown }> }; 
  attributes?: Array<{ name: string; value: unknown }>;
  custom?: { fields?: Record<string, unknown> } | Record<string, unknown>;
} | null | undefined): boolean {
  if (!item) return false;

  // Check attributes (Commercetools structure)
  const attributes = item.variant?.attributes || item.attributes || [];
  const hasPassOptions = attributes.some((attr) => attr.name === 'options');
  const hasPassBenefits = attributes.some((attr) => attr.name === 'benefits');

  // Check custom fields (common Informa structure)
  const custom = item.custom;
  const customFields = (custom && typeof custom === 'object' && 'fields' in custom) 
    ? (custom.fields as Record<string, unknown>) 
    : (custom as Record<string, unknown>) || {};
    
  const hasSelectedOptionsList = Array.isArray(customFields['selectedOptionsList']) && customFields['selectedOptionsList'].length > 0;
  const hasPassType = Boolean(customFields['passType']);

  return hasPassOptions || hasPassBenefits || hasSelectedOptionsList || hasPassType;
}
